import type { Badge, BaseEvent, EventCheckoutData, EventItem } from "@/types";
import { unixTimeToDate } from "@/utils/unixTimeToDate";

// TODO split this file into BaseEventUtil and EventItemUtil

const DEFAULT_LANGUAGE = 'da';

type LocalizationKeys = keyof BaseEvent['localizations'][string];

export const getLocalizedEventValue = (
  eventItem: BaseEvent | EventCheckoutData,
  language: string = DEFAULT_LANGUAGE,
  key: LocalizationKeys,
  characterLimit: number = 0
): string => {
  let value = eventItem.localizations?.[language]?.[key] || Object.values(eventItem.localizations || {}).find(localization => localization[key])?.[key] || '';
  if( characterLimit > 0  && value.length > characterLimit ) {
    value = value.slice(0, characterLimit) + '...';
  }
  return value;
};

// Helper function to get the eventtime with GMT offset applied. This time "format" is referred to as safetickettime in the codebase.
export const getEventtime = (eventItem: BaseEvent): number => { return eventItem.eventtime + eventItem.eventtime_gmt_offset; };

// Helper function to get the salestart with GMT offset applied. This time "format" is referred to as safetickettime in the codebase.
export const getSalestart = (eventItem: BaseEvent): number => { return eventItem.salestart + eventItem.salestart_gmt_offset; };

// Helper function to get the saleend with GMT offset applied. This time "format" is referred to as safetickettime in the codebase.
export const getSaleend = (eventItem: BaseEvent): number => { return eventItem.saleend + eventItem.saleend_gmt_offset; };

// Please keep the badges list sorted by priority so its easy to crop the list in the components.
export function getBadgesForEvent(eventItem: BaseEvent, options?: { externalTicketsaleActive?: boolean }): Badge[] {
  const badges = [];

  const effectiveSoldout = (options?.externalTicketsaleActive && 'external_ticketsale_soldout' in eventItem)
    ? !!(eventItem as EventItem).external_ticketsale_soldout
    : eventItem.soldout;
  const saleEnded = !!eventItem.saleend && unixTimeToDate(eventItem.saleend) < new Date();

  if (effectiveSoldout && !saleEnded && eventItem.has_waitlist && !options?.externalTicketsaleActive) {
    badges.push({ localeKey: 'common.waitlist', class: 'badge-few-tickets' });
  }
  else if (effectiveSoldout) {
    badges.push({ localeKey: 'common.soldout', class: 'badge-soldout' });
  }
  else if (saleEnded) {
    badges.push({ localeKey: 'event.sale_ended', class: 'badge-sale-ended' });
  }
  if (!effectiveSoldout && eventItem.few_tickets) {
    badges.push({ localeKey: 'common.few_tickets', class: 'badge-few-tickets' });
  }
  if (
    eventItem.status_message_visible &&
    eventItem.status_message_text
  ) {
    badges.push({ string: eventItem.status_message_text, class: 'badge-status' }); // TODO: Add localization if needed
  }

  return badges;
}

// Returns badges with responsive visibility classes applied based on frontpage_event_text_* flags.
export function getBadgesWithVisibility(eventItem: EventItem, options?: { externalTicketsaleActive?: boolean }): Badge[] {
  const badges = getBadgesForEvent(eventItem, options);

  badges.forEach(badge => {
    if (badge.class === 'badge-soldout') {
      const cls = visibilityClass(
        eventItem.frontpage_event_text_desktop_soldout,
        eventItem.frontpage_event_text_mobile_soldout,
      );
      if (cls) badge.class += ' ' + cls;
    }
    if (badge.class === 'badge-few-tickets') {
      const cls = visibilityClass(
        eventItem.frontpage_event_text_desktop_few_tickets,
        eventItem.frontpage_event_text_mobile_few_tickets,
      );
      if (cls) badge.class += ' ' + cls;
    }
  });

  return badges;
}

function visibilityClass(desktop: boolean | undefined, mobile: boolean | undefined, showClass = 'md:flex'): string {
  if (desktop && !mobile) return `hidden ${showClass}`;
  if (!desktop && mobile) return 'md:hidden lg:hidden xl:hidden 2xl:hidden';
  if (!desktop && !mobile) return 'hidden';
  return '';
}

export function getCategoryVisibilityClass(eventItem: EventItem): string {
  return visibilityClass(eventItem.frontpage_event_text_desktop_categories, eventItem.frontpage_event_text_mobile_categories);
}

export function getDateVisibilityClass(eventItem: EventItem): string {
  return visibilityClass(eventItem.frontpage_event_text_desktop_date, eventItem.frontpage_event_text_mobile_date);
}

export function getVenueVisibilityClass(eventItem: EventItem): string {
  return visibilityClass(eventItem.frontpage_event_text_desktop_venue, eventItem.frontpage_event_text_mobile_venue);
}

export function getPriceVisibilityClass(eventItem: EventItem): string {
  return visibilityClass(eventItem.frontpage_event_text_desktop_price, eventItem.frontpage_event_text_mobile_price);
}

export function getEventNameVisibilityClass(eventItem: EventItem, listMode = false): string {
  const desktop = eventItem.frontpage_event_text_desktop_event_name;
  const mobile = eventItem.frontpage_event_text_mobile_event_name;
  const desktopClamp = listMode ? 'md:line-clamp-1' : 'md:line-clamp-2';

  if (desktop && !mobile) return `hidden md:block ${desktopClamp}`;
  if (!desktop && mobile) return 'line-clamp-2 md:hidden lg:hidden xl:hidden 2xl:hidden';
  if (desktop && mobile) return listMode ? 'line-clamp-2 md:line-clamp-1' : 'line-clamp-2';
  return 'hidden';
}
